import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, Loader } from "lucide-react";
import Button from "../components/Button";
import chatService from "../services/chat";
import { successToast, errorToast } from "../utils/toasters";
import { base64ToFile, isAuthenticated } from "../utils/helpers";

import ImagePickerDialog from "../components/profile_components/ImagePickerDialog";
import { faker } from "@faker-js/faker";
import { getAvatars, GetProfile } from "../services/profile";
import AgeRangeSlider from "../components/chat_components/AgeSlider";
const ChatOnboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedVibes, setSelectedVibes] = useState([]);
  const [chatName, setChatName] = useState("");
  const [gender, setGender] = useState("");
  const [ageBand, setAgeBand] = useState("28-32");
  const [interests, setInterests] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [vibes, setVibes] = useState(null);

  const [seeMore, setSeeMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ageRange, setAgeRange] = useState({ min: 25, max: 45 });

  const handleRangeChange = (min, max) => {
    setAgeRange({ min, max });
  };
  function handleToggleVibeSeeMore() {
    setSeeMore(prev => !prev);
  }

  useEffect(
    () => {
      async function fakeName() {
        if (!isAuthenticated()) {
          localStorage.setItem("target", "makeyaari-onboarding");
          navigate("/signin");
        }
        const profile = await GetProfile();
        if (
          profile.data &&
          profile.data.avatar_id &&
          profile.data.avatar_id <= 77
        ) {
          setProfilePicture(profile.data.avatarurl);
        } else {
          if (profile.data.gender && profile.data.gender != "") {
            const allAvatars = await getAvatars(profile.data.gender);
            const randomId = Math.floor(
              Math.random() * (allAvatars.avatars.length - 1)
            );
            setProfilePicture(allAvatars.avatars[randomId].avatarurl);
          } else if (gender != "") {
            const allAvatars = await getAvatars(gender);
            const randomId = Math.floor(
              Math.random() * (allAvatars.avatars.length - 1)
            );
            setProfilePicture(allAvatars.avatars[randomId].avatarurl);
          }
        }

        if (profile.data.gender) {
          setGender(profile.data.gender.toLowerCase());
          const fakerName = faker.person.fullName({
            sex: profile.data.gender.toLowerCase()
          });
          setChatName(fakerName);
        } else if (gender && gender != "") {
          const fakerName = faker.person.fullName({
            sex: gender.toLowerCase()
          });
          setChatName(fakerName);
        }
      }

      fakeName();
    },
    [gender]
  );

  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const handleAvatarClick = () => {
    setImagePickerOpen(true);
  };

  const closeImagePicker = () => {
    setImagePickerOpen(false);
  };

  const handleImageSelected = newImage => {
    setProfilePicture(newImage);
    closeImagePicker();
  };

  useEffect(() => {
    async function fetchBraggableInterests() {
      try {
        const interests = await chatService.getInterests();
        setInterests(interests.data);
      } catch (error) {
        console.error("Error while fetching braggable interest: ", error);
      }
    }
    fetchBraggableInterests();
    async function fetchVibes() {
      try {
        const vibes = await chatService.getVibes();
        setVibes(vibes.data);
      } catch (error) {
        console.error("Error while fetching vibes : ", error);
      }
    }
    fetchVibes();
  }, []);

  const totalSteps = 5;

  const toggleInterest = interestId => {
    setSelectedInterests(prev => {
      if (prev.includes(interestId)) {
        return prev.filter(id => id !== interestId);
      } else if (prev.length < 5) {
        return [...prev, interestId];
      }
      return prev;
    });
  };

  const toggleVibe = vibeId => {
    setSelectedVibes([vibeId]);
  };

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      try {
        if (gender.trim() === "") {
          gender = "male";
        }

        setIsSubmitting(true);
        /*
        const payload = new FormData();

        payload.append("selectedVibes", selectedVibes);
        payload.append("gender", gender);
        payload.append("selectedInterests", selectedInterests);
        payload.append("chatName", chatName);
        payload.append("ageBand", `${ageRange.min}-${ageRange.max}`);
        payload.append("geohash", localStorage.getItem("geohash") || "9v6m");
        payload.append("profile_picture", profilePicture);
        */
        const payload = {
          selectedVibes: selectedVibes,
          gender: gender,
          selectedInterests: selectedInterests,
          chatName: chatName,
          ageBand: `${ageRange.min}-${ageRange.max}`,
          geohash: localStorage.getItem("geohash") || "9v6m",
          profile_picture: profilePicture
        };

        await chatService.saveProfile(payload);
        successToast("Profile Saved Successfully");
        navigate("/makeyaarichat");
      } catch (error) {
        console.error("Error while saving profile: ", error);
        errorToast(error.response.data.error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    setCurrentStep(2); // Skip to interests
  };

  const canProceed = () => {
    switch (currentStep) {
      case 2:
        return selectedInterests.length > 0;
      case 3:
        return selectedVibes.length > 0;
      case 4:
        return chatName.trim() !== "";
      default:
        return true;
    }
  };

  // Step 0: Welcome Screen
  if (currentStep === 0) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/chat/images/dreamy-blue-bg.png"
            alt="Dreamy connection background"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 z-20 p-4">
          <div className="w-full bg-white/20 rounded-full h-1">
            <div
              className="bg-white h-1 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep + 1) / totalSteps * 100}%` }}
            />
          </div>
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col min-h-screen">
          {/* Mobile Layout */}
          <div className="flex-1 flex flex-col justify-end p-6 pb-8 md:hidden">
            <div className="space-y-6">
              <div className="space-y-4">
                <h1 className="text-white text-4xl font-bold leading-tight">
                  Discover the connection that suits you best
                </h1>
                <p className="text-white/90 text-lg">
                  Connect through shared experiences.
                </p>
              </div>

              <div className="space-y-4">
                <Button size="lg" onClick={handleNext} className="w-full">
                  Next
                </Button>
                <button
                  onClick={handleSkip}
                  className="w-full text-white/80 hover:text-white text-lg font-medium"
                >
                  Skip
                </button>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex flex-col min-h-screen">
            <div className="flex-1 flex items-center justify-center px-8">
              <div className="max-w-2xl mx-auto text-center space-y-8">
                <div className="space-y-6">
                  <h1 className="text-white text-5xl lg:text-6xl font-bold leading-tight">
                    Discover the connection that suits you best
                  </h1>
                  <p className="text-white/90 text-xl lg:text-2xl">
                    Connect through shared experiences.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" onClick={handleNext}>
                    Next
                  </Button>
                  <button
                    onClick={handleSkip}
                    className="text-white/80 hover:text-white text-lg font-medium px-8 py-4"
                  >
                    Skip
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: True Stories Screen
  if (currentStep === 1) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/chat/images/phone-app-bg.png"
            alt="Hand holding phone with dating app interface"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 z-20 p-4">
          <div className="w-full bg-white/20 rounded-full h-1">
            <div
              className="bg-white h-1 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep + 1) / totalSteps * 100}%` }}
            />
          </div>
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col min-h-screen">
          {/* Mobile Layout */}
          <div className="flex-1 flex flex-col justify-end p-6 pb-8 md:hidden">
            <div className="space-y-6">
              <div className="space-y-4">
                <h1 className="text-white text-4xl font-bold leading-tight">
                  True stories, Real impact
                </h1>
                <p className="text-white/90 text-lg">
                  Connect through shared experiences.
                </p>
              </div>

              <div className="space-y-4">
                <Button size="lg" onClick={handleNext} className="w-full">
                  Next
                </Button>
                <button
                  onClick={handleSkip}
                  className="w-full text-white/80 hover:text-white text-lg font-medium"
                >
                  Skip
                </button>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex flex-col min-h-screen">
            <div className="flex-1 flex items-center justify-center px-8">
              <div className="max-w-2xl mx-auto text-center space-y-8">
                <div className="space-y-6">
                  <h1 className="text-white text-5xl lg:text-6xl font-bold leading-tight">
                    True stories, Real impact
                  </h1>
                  <p className="text-white/90 text-xl lg:text-2xl">
                    Connect through shared experiences.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" onClick={handleNext}>
                    Next
                  </Button>
                  <button
                    onClick={handleSkip}
                    className="text-white/80 hover:text-white text-lg font-medium px-8 py-4"
                  >
                    Skip
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Interests Selection
  if (currentStep === 2) {
    return (
      <div className="min-h-screen bg-white">
        {/* Progress Bar */}
        <div className="p-4 pb-0">
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div
              className="bg-[#7B189F] h-1 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep + 1) / totalSteps * 100}%` }}
            />
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden min-h-screen flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-4 p-6 pb-4">
            <button onClick={handleBack} className="text-gray-600">
              <ArrowLeft size={24} />
            </button>
            <div className="text-sm text-gray-500">
              Step {currentStep + 1} of {totalSteps}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 pb-4">
            <div className="space-y-6">
              <div className="space-y-4">
                <h1 className="text-3xl font-bold text-gray-900">
                  What's Your Braggable Interest?
                </h1>
                <p className="text-gray-600 text-lg">
                  Pick a single feature to showcase your passion in your
                  profile.
                </p>
              </div>

              {/* Interest Pills Grid */}
              <div className="grid grid-cols-2 gap-3">
                {interests &&
                  interests.map(interest => {
                    const isSelected = selectedInterests.includes(interest.id);
                    return (
                      <button
                        key={interest.id}
                        onClick={() => toggleInterest(interest.id)}
                        className={`flex items-center gap-2 px-4 py-3 rounded-full border transition-colors ${
                          isSelected
                            ? "bg-black text-white border-black"
                            : "bg-white text-gray-900 border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <span className="text-lg">{interest.emoji}</span>
                        <span className="font-medium text-sm">
                          {interest.title}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <div className="p-6">
            <Button
              size="lg"
              disabled={!canProceed()}
              onClick={handleNext}
              className="w-full"
            >
              Continue
            </Button>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:block min-h-screen">
          <div className="flex items-center justify-center min-h-screen p-8">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                  <button onClick={handleBack} className="text-gray-600">
                    <ArrowLeft size={24} />
                  </button>
                  <div className="text-sm text-gray-500">
                    Step {currentStep + 1} of {totalSteps}
                  </div>
                </div>

                <div className="text-center space-y-4">
                  <h1 className="text-5xl font-bold text-gray-900">
                    What's Your Braggable Interest?
                  </h1>
                  <p className="text-gray-600 text-xl">
                    Choose up to five to feature your passion in your profile.
                  </p>
                </div>

                {/* Interest Pills Grid */}
                <div className="grid grid-cols-3 lg:grid-cols-4 gap-4">
                  {interests &&
                    interests.map(interest => {
                      const isSelected = selectedInterests.includes(
                        interest.id
                      );
                      return (
                        <button
                          key={interest.id}
                          onClick={() => toggleInterest(interest.id)}
                          className={`flex items-center gap-3 px-6 py-4 rounded-full border transition-colors ${
                            isSelected
                              ? "bg-black text-white border-black"
                              : "bg-white text-gray-900 border-gray-300 hover:border-gray-400"
                          }`}
                        >
                          <span className="text-xl">{interest.emoji}</span>
                          <span className="font-medium">{interest.title}</span>
                        </button>
                      );
                    })}
                </div>

                {/* Continue Button */}
                <div className="flex justify-center pt-8">
                  <Button
                    size="lg"
                    disabled={!canProceed()}
                    onClick={handleNext}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Today's Vibes
  if (currentStep === 3) {
    return (
      <div className="min-h-screen bg-white">
        {/* Progress Bar */}
        <div className="p-4 pb-0">
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div
              className="bg-[#7B189F] h-1 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep + 1) / totalSteps * 100}%` }}
            />
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden min-h-screen flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-4 p-6 pb-4">
            <button onClick={handleBack} className="text-gray-600">
              <ArrowLeft size={24} />
            </button>
            <div className="text-sm text-gray-500">
              Step {currentStep + 1} of {totalSteps}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 pb-4">
            <div className="space-y-6">
              <div className="space-y-4">
                <h1 className="text-3xl font-bold text-gray-900">
                  What's Your Todays Vibes?
                </h1>
                <p className="text-gray-600 text-lg">
                  Choose one vibe to feature your passion in your profile.
                </p>
              </div>

              {/* Vibes Pills Grid */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {vibes &&
                    (seeMore ? vibes : vibes.slice(0, 6)).map(vibe => {
                      const isSelected = selectedVibes.includes(vibe.id);
                      return (
                        <button
                          key={vibe.id}
                          onClick={() => toggleVibe(vibe.id)}
                          className={`flex items-center gap-2 px-4 py-3 rounded-full border transition-colors text-left ${
                            isSelected
                              ? "bg-black text-white border-black"
                              : "bg-white text-gray-900 border-gray-300 hover:border-gray-400"
                          }`}
                        >
                          <span className="text-lg">{vibe.emoji}</span>
                          <span className="font-medium text-sm">
                            {vibe.title}
                          </span>
                        </button>
                      );
                    })}
                </div>
                {/* See More Button */}
                <div className="flex justify-start">
                  <button
                    onClick={handleToggleVibeSeeMore}
                    className="px-6 py-2 rounded-full border border-gray-300 text-gray-700 hover:border-gray-400 transition-colors"
                  >
                    {!seeMore ? "See More" : "See Less"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <div className="p-6">
            <Button
              size="lg"
              disabled={!canProceed()}
              onClick={handleNext}
              className="w-full"
            >
              Continue
            </Button>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:block min-h-screen">
          <div className="flex items-center justify-center min-h-screen p-8">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                  <button onClick={handleBack} className="text-gray-600">
                    <ArrowLeft size={24} />
                  </button>
                  <div className="text-sm text-gray-500">
                    Step {currentStep + 1} of {totalSteps}
                  </div>
                </div>

                <div className="text-center space-y-4">
                  <h1 className="text-5xl font-bold text-gray-900">
                    What's Your Todays Vibes?
                  </h1>
                  <p className="text-gray-600 text-xl">
                    Pick a single feature to showcase your passion in your
                    profile.
                  </p>
                </div>

                {/* Vibes Pills Grid */}
                <div className="space-y-6">
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    {vibes &&
                      (seeMore ? vibes : vibes.slice(0, 6)).map(vibe => {
                        const isSelected = selectedVibes.includes(vibe.id);
                        return (
                          <button
                            key={vibe.id}
                            onClick={() => toggleVibe(vibe.id)}
                            className={`flex items-center gap-3 px-6 py-4 rounded-full border transition-colors ${
                              isSelected
                                ? "bg-black text-white border-black"
                                : "bg-white text-gray-900 border-gray-300 hover:border-gray-400"
                            }`}
                          >
                            <span className="text-xl">{vibe.emoji}</span>
                            <span className="font-medium">{vibe.title}</span>
                          </button>
                        );
                      })}
                  </div>

                  {/* See More Button */}
                  <div className="flex justify-center">
                    <button
                      onClick={handleToggleVibeSeeMore}
                      className="px-8 py-3 rounded-full border border-gray-300 text-gray-700 hover:border-gray-400 transition-colors"
                    >
                      {!seeMore ? "See More" : "See Less"}
                    </button>
                  </div>
                </div>

                {/* Continue Button */}
                <div className="flex justify-center pt-8">
                  <Button
                    size="lg"
                    disabled={!canProceed()}
                    onClick={handleNext}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 4: Chat Profile Setup
  return (
    <div className="min-h-screen bg-white">
      {isSubmitting && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <Loader className="animate-spin" width={64} height={64} />
        </div>
      )}

      {/* Progress Bar */}
      <div className="p-4 pb-0">
        <div className="w-full bg-gray-200 rounded-full h-1">
          <div
            className="bg-[#7B189F] h-1 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep + 1) / totalSteps * 100}%` }}
          />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden min-h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 p-6 pb-4">
          <button onClick={handleBack} className="text-gray-600">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Chat Profile</h1>
          <div className="ml-auto text-sm text-gray-500">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>

        {/* Content */}

        <div className="flex-1 px-6">
          <div className="space-y-6">
            <p className="text-gray-600 text-sm leading-relaxed">
              This profile will be used for new chats until you decide to reveal
              your identity, it's a one time setup and helps keep your
              conversation smooth and private.
            </p>

            {/* Profile Photo */}
            <div className="flex justify-center">
              <div className="w-32 h-32 rounded-full overflow-hidden">
                <img
                  src="/chat/images/profile-photo.png"
                  alt="Profile photo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-6">
              {/* Chat Name */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  Chat Name For Today
                </label>
                <input
                  type="text"
                  onChange={e => setChatName(e.target.value)}
                  value={chatName}
                  className="w-full p-4 border border-gray-200 rounded-lg text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                />
              </div>

              {/* Age Band */}
              <AgeRangeSlider
                minAge={18}
                maxAge={50}
                onRangeChange={handleRangeChange}
              />
              {!gender && (
                <div className="relative">
                  <select
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7B189F] appearance-none"
                    name="gender"
                    value={gender || ""}
                    onChange={value => {
                      if (value === "Male") {
                        console.log("value==", value);
                        setGender("female");
                      } else {
                        setGender("male");
                      }
                    }}
                  >
                    <option value="">
                      Select who would you like to chat with:
                    </option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                  <ChevronDown
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    size={18}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div className="p-6">
          <Button
            size="lg"
            disabled={!canProceed()}
            onClick={handleNext}
            className="w-full"
          >
            Complete Setup
          </Button>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:block min-h-screen">
        <div className="flex items-center justify-center min-h-screen p-8">
          <div className="max-w-2xl mx-auto">
            <div className="space-y-8">
              {/* Header */}
              <div className="flex items-center gap-4">
                <button onClick={handleBack} className="text-gray-600">
                  <ArrowLeft size={24} />
                </button>
                <h1 className="text-3xl font-bold text-gray-900">
                  Chat Profile
                </h1>
                <div className="ml-auto text-sm text-gray-500">
                  Step {currentStep + 1} of {totalSteps}
                </div>
              </div>

              <p className="text-gray-600 text-lg leading-relaxed">
                This profile will be used for new chats until you decide to
                reveal your identity, it's a one time setup and helps keep your
                conversation smooth and private.
              </p>

              {/* Profile Photo */}
              <div className="flex justify-center">
                <div
                  onClick={() => {
                    console.log(
                      "cannot select picture for now will update later based on design"
                    );
                    // handleAvatarClick()
                  }}
                  className="w-40 cursor-pointer h-40 rounded-full overflow-hidden"
                >
                  <img
                    src={profilePicture || "/placeholder.svg"}
                    alt="Profile photo"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-6">
                {/* Chat Name */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Chat Name For Today
                  </label>
                  <input
                    type="text"
                    value={chatName}
                    onChange={e => setChatName(e.target.value)}
                    className="w-full p-4 border border-gray-200 rounded-lg text-gray-900 font-medium text-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                  />
                </div>

                {/* Age Band */}
                <AgeRangeSlider
                  minAge={18}
                  maxAge={50}
                  onRangeChange={handleRangeChange}
                />
                {!gender && (
                  <div className="relative">
                    <select
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7B189F] appearance-none"
                      name="gender"
                      value={gender || ""}
                      onChange={e => {
                        console.log("value ", e.target.value);
                        if (e.target.value === "Male") {
                          console.log("male selected", e.target.value);
                          setGender("female");
                        } else {
                          setGender("male");
                        }
                      }}
                    >
                      <option value="">
                        Please First Select who would you like to chat with:
                      </option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                    <ChevronDown
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                      size={18}
                    />
                  </div>
                )}
              </div>

              {/* Continue Button */}
              <div className="flex justify-center pt-8">
                <Button size="lg" disabled={!canProceed()} onClick={handleNext}>
                  Complete Setup
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {imagePickerOpen && (
        <ImagePickerDialog
          onClose={closeImagePicker}
          onImageSelected={handleImageSelected}
        />
      )}
    </div>
  );
};

export default ChatOnboarding;
